import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Warning, CaretDown, CaretRight, ArrowsClockwise, LinkSimple, Trash,
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import ProductoCombobox from "./ProductoCombobox";
import api from "../lib/api";

/**
 * Banner de lotes huérfanos en Stock.
 *  - Lista los lotes cuyo `producto_id` / `producto_normalizado` no matchea
 *    con ningún producto del catálogo (Productos y Precios).
 *  - Permite vincularlos a un producto existente o eliminarlos (si no tienen
 *    consumos).
 *  - `onChanged` se dispara después de vincular/eliminar para que el padre
 *    refresque el stock.
 */
export default function StockOrphansBanner({ onChanged }) {
  const [orphans, setOrphans] = useState([]);
  const [total, setTotal] = useState(0);
  const [kilosTot, setKilosTot] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null); // orphan seleccionado para vincular
  const [linkProd, setLinkProd] = useState(null);     // { id, codigo, nombre }
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);     // id del lote borrandose

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock/orphans");
      setOrphans(data?.orphans || []);
      setTotal(data?.total || 0);
      setKilosTot(data?.kilos_totales || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la lista de huérfanos");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vincular = async () => {
    if (!linkTarget || !linkProd?.id) return toast.warning("Elegí un producto del catálogo para vincular");
    setSaving(true);
    try {
      await api.post(`/stock/orphans/${linkTarget.lote_id}/vincular`, { producto_id: linkProd.id });
      toast.success(`Lote ${linkTarget.numero} vinculado a "${linkProd.nombre}"`);
      setLinkTarget(null); setLinkProd(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setSaving(false); }
  };

  const eliminar = async (o) => {
    if (o.tiene_consumos) {
      return toast.error("El lote tiene consumos. No se puede borrar sin romper la trazabilidad. Vinculalo a un producto en su lugar.");
    }
    if (!window.confirm(`¿Eliminar el lote huérfano "${o.descripcion}" (${o.kilos_disponibles} kg)?`)) return;
    setDeleting(o.lote_id);
    try {
      await api.delete(`/stock/orphans/${o.lote_id}`);
      toast.success("Lote eliminado");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally { setDeleting(null); }
  };

  if (total === 0 && !loading) return null;

  const tipoLabel = (t) => ({
    ingreso: "Ingreso mercadería",
    recepcion: "Recepción",
    transformacion: "Transformación",
    venta_sin_stock: "Venta sin stock",
    receta_auto: "Auto-receta",
    compra_directa: "Compra directa",
    ajuste_manual: "Ajuste manual",
    inventario: "Inventario",
  }[t] || (t || "—"));

  return (
    <div
      className="border border-red-300 bg-red-50/70 rounded-sm mb-4"
      data-testid="stock-orphans-banner"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left"
        data-testid="btn-toggle-orphans"
      >
        {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        <Warning size={18} weight="fill" className="text-red-600" />
        <span className="text-[11px] uppercase font-mono tracking-widest text-red-800 font-bold">
          Alerta · Productos sin catálogo
        </span>
        <span className="text-xs font-mono text-red-900">
          {total} lote{total === 1 ? "" : "s"} con {kilosTot.toLocaleString("es-AR", { maximumFractionDigits: 3 })} kg no vinculados a "Productos y Precios"
        </span>
        <Button
          size="sm" variant="outline"
          onClick={(e) => { e.stopPropagation(); load(); }}
          disabled={loading}
          className="ml-auto text-xs h-7 gap-1 border-red-300 text-red-700 hover:bg-red-100"
          data-testid="btn-refresh-orphans"
        >
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          Refrescar
        </Button>
      </button>
      {open && (
        <div className="border-t border-red-200 p-3">
          <div className="text-[11px] text-red-900 mb-2 font-mono">
            Estos lotes aparecen en el stock pero no matchean con ningún producto del catálogo.
            Vinculalos a un producto existente para que aparezcan bien agrupados, o eliminalos si
            son basura de test/legacy.
          </div>
          <div className="max-h-80 overflow-auto border border-red-200 bg-white">
            <table className="w-full text-xs font-mono">
              <thead className="bg-red-100 text-red-900 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 w-28">Nº Lote</th>
                  <th className="text-left px-2 py-1">Descripción</th>
                  <th className="text-left px-2 py-1 w-32">Origen</th>
                  <th className="text-left px-2 py-1 w-40">Referencia</th>
                  <th className="text-right px-2 py-1 w-20">Kg</th>
                  <th className="text-right px-2 py-1 w-44">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((o) => (
                  <tr key={o.lote_id} className="border-t border-neutral-100">
                    <td className="px-2 py-1 font-bold text-[#921A0E]">{o.numero}</td>
                    <td className="px-2 py-1">
                      {o.descripcion}
                      {o.tiene_consumos && (
                        <Badge variant="outline" className="ml-1 text-[9px] text-orange-700 border-orange-300">
                          con consumos
                        </Badge>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <Badge variant="outline" className="text-[10px]">{tipoLabel(o.tipo_origen)}</Badge>
                    </td>
                    <td className="px-2 py-1 text-neutral-600 truncate max-w-[10rem]">
                      {o.origen_ref?.proveedor_nombre
                        || o.origen_ref?.lote_origen_numero
                        || o.origen_ref?.ref_tipo
                        || "—"}
                    </td>
                    <td className={`px-2 py-1 text-right font-bold ${o.kilos_disponibles < 0 ? "text-red-700" : ""}`}>
                      {o.kilos_disponibles.toLocaleString("es-AR", { maximumFractionDigits: 3 })}
                    </td>
                    <td className="px-2 py-1 text-right space-x-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setLinkTarget(o); setLinkProd(null); }}
                        className="text-xs h-7 gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`btn-vincular-${o.lote_id}`}
                      >
                        <LinkSimple size={12} weight="bold" /> Vincular
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => eliminar(o)}
                        disabled={deleting === o.lote_id || o.tiene_consumos}
                        className="text-xs h-7 gap-1 border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-40"
                        data-testid={`btn-borrar-${o.lote_id}`}
                        title={o.tiene_consumos ? "Tiene consumos — usar Vincular" : ""}
                      >
                        <Trash size={12} weight="bold" /> Borrar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog Vincular */}
      <Dialog open={!!linkTarget} onOpenChange={(v) => !v && !saving && setLinkTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-vincular-orphan">
          <DialogHeader>
            <DialogTitle>Vincular lote a producto del catálogo</DialogTitle>
            <DialogDescription>
              Este lote ({linkTarget?.numero} · {linkTarget?.descripcion}) va a quedar linkeado
              al producto seleccionado. A partir de ahí aparece con el nombre canónico en Stock
              y matchea en ventas por SKU.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="text-xs uppercase font-mono text-neutral-500 mb-1">Producto del catálogo</div>
            <ProductoCombobox
              kind="productos"
              value={linkProd ? { codigo: linkProd.codigo, nombre: linkProd.nombre } : null}
              onSelect={(p) => setLinkProd(p)}
              placeholder="Buscar producto por nombre o código…"
              testid="vincular-orphan-combo"
            />
            {linkProd && (
              <div className="mt-2 text-[11px] text-neutral-600 font-mono">
                Vas a linkear <strong>{linkTarget?.descripcion}</strong> → <strong>{linkProd.nombre}</strong>
                {linkProd.codigo && <> (código {linkProd.codigo})</>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTarget(null)} disabled={saving}>Cancelar</Button>
            <Button
              onClick={vincular}
              disabled={saving || !linkProd?.id}
              className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
              data-testid="btn-confirm-vincular"
            >
              <LinkSimple size={14} weight="bold" /> {saving ? "Vinculando…" : "Vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
